export const nth = (d: number): string => {
  if (d > 3 && d < 21) return `${d}th`
  switch (d % 10) {
    case 1:
      return `${d}st`
    case 2:
      return `${d}nd`
    case 3:
      return `${d}rd`
    default:
      return `${d}th`
  }
}

export const fullMonth = (m: number): string => {
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  return months[m]
}

export const formatDate = (value: Date | string | null | undefined, isUTC: boolean = false): string => {
  if (value == null) return '';
  const parsedDate = value instanceof Date ? value : new Date(value);

    return parsedDate.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      timeZone: isUTC ? 'UTC' : undefined,
    })
}

export const formatReadableDate = (value: Date | string | null | undefined, isUTC: boolean = false): string => {
  if (value == null) return '';
  const parsedDate = value instanceof Date ? value : new Date(value);
  if (isNaN(parsedDate.getTime())) return '';

  return parsedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: isUTC ? 'UTC' : undefined,
  })
}

export const formatDateTime = (ISOString: Date = new Date(), isUTC: boolean = false): string =>
  new Date(ISOString).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZone: isUTC ? 'UTC' : undefined,
  })

// Formats a UTC date string into a readable date string of the format: "MM/DD/YY" eg "01/01/21"
export const formatUTCDate = (value: string | Date | null | undefined): string => {
  if (value == null) return '';
  const date = value instanceof Date ? value : new Date(value);

  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${String(date.getUTCFullYear()).substr(2)}`
}

// Month/day only — year omitted when statement date provides context (e.g. billing period columns)
export const formatUTCMonthDay = (value: string | Date | null | undefined): string => {
  if (value == null) return ''
  const date = value instanceof Date ? value : new Date(value)
  return `${date.getUTCMonth() + 1}/${date.getUTCDate()}`
}

export const formatBillingPeriod = (start: string | Date, end: string | Date): string => {
  return `${formatUTCMonthDay(start)} - ${formatUTCMonthDay(end)}`
}

export const formatUTCDateTime = (ISOString: Date = new Date()): string => {
  const date = new Date(ISOString)

  // padStart will ensure that single digit months, dates, hours, minutes and seconds are prepended with a zero
  const formattedDate = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}T${String(
    date.getUTCHours()
  ).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}:${String(date.getUTCSeconds()).padStart(2, '0')}.${String(
    date.getUTCMilliseconds()
  ).padStart(3, '0')}Z`

  return formattedDate
}

// Formats a UTC date string into a readable date string of the format: "Month Day, Year"
// eg. "Jan 1, 2021"
export const formatUTCDetailedDate = (ISOString = new Date()): string => {
  const shortMonthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  const date = new Date(ISOString);

  // Extract UTC date components
  const year = date.getUTCFullYear();
  const month = shortMonthNames[date.getUTCMonth()];
  const day = date.getUTCDate();

  return `${month} ${day}, ${year}`;
}

/**
 * Creates a Date at local midnight from a UTC ISO date string.
 * Use when passing API date strings (e.g. "2026-02-15T00:00:00.000Z") to date pickers
 * so the picker displays the correct calendar date regardless of user timezone.
 * Without this, "2026-02-15T00:00:00.000Z" in PST would display as Feb 14.
 */
export const dateFromUTCDateString = (isoString: string | null | undefined): Date | null => {
  if (isoString == null) return null;
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

/**
 * Formats a Date to YYYY-MM-DD for API using the date's local calendar components.
 * Use when the user selects a date in a picker (which gives a Date at local midnight).
 * Avoids timezone shift: toISOString().split('T')[0] can return the wrong date for
 * users in positive UTC offset timezones.
 */
export const formatDateForApi = (date: Date | null | undefined): string | null => {
  if (date == null) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Sentinel end date meaning “no planned end” (program onboarding, VNEM; matches backend `FUTURE_END_DATE_ONLY`). */
export const NO_PLANNED_END_DATE = '2030-12-31'

/** Today's UTC calendar day as `yyyy-mm-dd`. */
export const utcTodayDateOnlyString = (): string => new Date().toISOString().slice(0, 10)

/** ISO date-only strings (`yyyy-mm-dd`) compare correctly lexicographically. */
export const isEndDateOnOrAfterStart = (endDate: string, startDate: string): boolean => {
  if (endDate === '' || startDate === '') return true
  return endDate >= startDate
}

export const isNoPlannedEndDate = (endDate: string | null | undefined): boolean => {
  if (endDate == null || endDate === '') return true
  const normalized = endDate.length >= 10 ? endDate.slice(0, 10) : endDate
  return normalized === NO_PLANNED_END_DATE
}

/** Coerce omitted end dates to {@link NO_PLANNED_END_DATE} for range overlap checks. */
export const coerceDateOnlyEndDate = (endDate: string | null | undefined): string => {
  if (endDate == null || endDate === '') return NO_PLANNED_END_DATE
  return endDate.length >= 10 ? endDate.slice(0, 10) : endDate
}

/** True when [start, end) windows share at least one in-effect UTC day (onboarding overlap). */
export const isoDateRangesOverlap = (
  aStart: string,
  aEnd: string | null | undefined,
  bStart: string,
  bEnd: string | null | undefined
): boolean => {
  const aEndResolved = coerceDateOnlyEndDate(aEnd)
  const bEndResolved = coerceDateOnlyEndDate(bEnd)
  return aStart < bEndResolved && bStart < aEndResolved
}

export const formatTimeInMilliseconds = (ISOString: string): number => {
  const date = new Date(ISOString)
  return date.getTime()
}

export const formatCurrency = (value: number | null): string => {
  if (value === null || isNaN(Number(value))) {
    return ''
  }
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}

export const formatPhoneNumber = (value: string = ''): string => {
  const numericValue = value?.replace('+1', '')?.replace(/\D/g, '')
  const match = numericValue.match(/^(\d{3})(\d{3})(\d{4})/)
  return match !== null ? `(${match[1]}) ${match[2]}-${match[3]}` : value
}

export const formatEIN = (value: string = ''): string => {
  const numericValue = value?.replace(/\D/g, '')
  const match = numericValue.match(/^(\d{2})(\d{7})/)
  return match !== null ? `${match[1]}-${match[2]}` : value
}

export const formatPostalCode = (value: string = ''): string => {
  const numericValue = value.replace(/\D/g, '')
  const match2 = numericValue.match(/^(\d{5})(\d{4})/)
  const match1 = numericValue.match(/^(\d{5})/)
  return match2 != null ? `${match2[1]}-${match2[2]}` : match1 != null ? match1[1] : value
}

export const makeEnumReadable = (value: string | null): string => {
  if (value === null || typeof value !== 'string') {
    return ''
  }
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase().replaceAll('_', ' ')
}

export const formatFileSize = (sizeInBytes: number): string => {
  const sizeInKB = Math.floor(sizeInBytes / 1024)
  const sizeInMB = Math.floor(sizeInKB / 1024)
  const sizeInGB = Math.floor(sizeInMB / 1024)
  const sizeInTB = Math.floor(sizeInGB / 1024)

  if (sizeInTB >= 1) {
    return `${sizeInTB.toLocaleString('en-US')} TB`
  } else if (sizeInGB >= 1) {
    return `${sizeInGB.toLocaleString('en-US')} GB`
  } else if (sizeInMB >= 1) {
    return `${sizeInMB.toLocaleString('en-US')} MB`
  } else if (sizeInKB >= 1) {
    return `${sizeInKB.toLocaleString('en-US')} KB`
  } else {
    return `${sizeInBytes.toLocaleString('en-US')} B`
  }
}

export const formatTitleCase = (str: string): string => {
  return str
    .toLowerCase()
    .split(/\s|_|-/) // Split on spaces, underscores, or dashes
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Extract a user-facing error message from an API error (e.g. axios error).
 * Handles NestJS-style payloads: message (string | string[]), error, errorMessage.
 */
export const getApiErrorMessage = (e: unknown): string => {
  const anyE = e as any
  const data = anyE?.response?.data
  if (data && typeof data === 'object') {
    if (typeof data.message === 'string') return data.message
    if (Array.isArray(data.message)) return data.message.join(' ')
    if (typeof data.error === 'string') return data.error
    if (typeof data.errorMessage === 'string') return data.errorMessage
  }
  if (typeof anyE?.message === 'string') return anyE.message
  return 'Unknown error'
}